import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Tag, Trash2, X, Calendar as CalIcon, Percent, Coins, DollarSign, ToggleLeft, ToggleRight } from "lucide-react";
import { api, errorMessage } from "../lib/api";

const KINDS = [
  { value: "percent_off",             label: "% off",            icon: Percent,    suffix: "%",      help: "Discount = subtotal × value%" },
  { value: "flat_off",                label: "$ off",            icon: DollarSign, suffix: "$",      help: "Flat dollars off subtotal" },
  { value: "bonus_points_per_cookie", label: "Pts per cookie",   icon: Coins,      suffix: "pts",    help: "Bonus pts added after payment" },
];

const KIND_LABEL = Object.fromEntries(KINDS.map(k => [k.value, k.label]));
const KIND_ICON  = Object.fromEntries(KINDS.map(k => [k.value, k.icon]));

const EMPTY_FORM = { code: "", kind: "percent_off", value: "", description: "", active: true, expires_at: "", usage_cap: "" };

export default function AdminPromoCodes() {
  const [codes, setCodes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/admin/promo-codes").then(r => setCodes(r.data || [])).catch(e => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY_FORM); setEditingId(null); setShowForm(true); };
  const openEdit = (c) => {
    setForm({ code: c.code, kind: c.kind, value: String(c.value), description: c.description || "", active: c.active, expires_at: c.expires_at || "", usage_cap: c.usage_cap == null ? "" : String(c.usage_cap) });
    setEditingId(c.id);
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setForm(EMPTY_FORM); setEditingId(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || form.value === "") { toast.error("Code and value are required"); return; }
    const payload = {
      code: form.code.trim().toUpperCase(),
      kind: form.kind,
      value: parseFloat(form.value),
      description: form.description.trim(),
      active: !!form.active,
      expires_at: form.expires_at || null,
      usage_cap: form.usage_cap === "" ? null : parseInt(form.usage_cap, 10),
    };
    if (Number.isNaN(payload.value)) { toast.error("Value must be a number"); return; }
    setSubmitting(true);
    try {
      if (editingId) {
        await api.put(`/admin/promo-codes/${editingId}`, payload);
        toast.success("Code updated");
      } else {
        await api.post("/admin/promo-codes", payload);
        toast.success("Code created");
      }
      closeForm();
      load();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSubmitting(false); }
  };

  const toggleActive = async (c) => {
    try {
      await api.put(`/admin/promo-codes/${c.id}`, { ...c, active: !c.active, expires_at: c.expires_at || null, usage_cap: c.usage_cap });
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete code ${c.code}? Customers who already used it keep their reward — this just stops new uses.`)) return;
    try { await api.delete(`/admin/promo-codes/${c.id}`); load(); toast.success("Code deleted"); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div data-testid="admin-promo-codes">
      <div className="mb-6 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl flex items-center gap-2"><Tag className="w-7 h-7 text-[#FF1F87]" /> Promo Codes</h1>
          <p className="text-gray-500 mt-1 text-sm">Create codes for discounts or bonus points. Each customer can use each code once.</p>
        </div>
        <button onClick={openCreate} className="btn-pink !py-2 !px-4 text-sm flex items-center gap-2" data-testid="admin-promo-new-btn">
          <Plus className="w-4 h-4" /> New Code
        </button>
      </div>

      {codes.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-2xl p-8 text-center">
          <Tag className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">No promo codes yet. Tap "New Code" to create your first one.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-widest text-gray-500">
                <tr>
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Value</th>
                  <th className="px-4 py-3">Used</th>
                  <th className="px-4 py-3">Cap</th>
                  <th className="px-4 py-3">Expires</th>
                  <th className="px-4 py-3">Active</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {codes.map(c => {
                  const Icon = KIND_ICON[c.kind] || Tag;
                  const suffix = c.kind === "percent_off" ? "%" : c.kind === "flat_off" ? "$" : " pts/cookie";
                  return (
                    <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`promo-row-${c.code}`}>
                      <td className="px-4 py-3 font-display font-extrabold tracking-widest">
                        <div>{c.code}</div>
                        {c.description ? <div className="text-[10px] text-gray-400 font-normal tracking-normal mt-0.5 max-w-[200px] truncate" title={c.description}>{c.description}</div> : null}
                      </td>
                      <td className="px-4 py-3 text-gray-600 flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {KIND_LABEL[c.kind]}</td>
                      <td className="px-4 py-3 font-bold">{c.kind === "flat_off" ? `$${c.value.toFixed(2)}` : `${c.value}${suffix}`}</td>
                      <td className="px-4 py-3 text-gray-600">{c.usage_count || 0}</td>
                      <td className="px-4 py-3 text-gray-600">{c.usage_cap ?? "∞"}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{c.expires_at || "—"}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => toggleActive(c)} className={c.active ? "text-emerald-600" : "text-gray-400"} aria-label="toggle active" data-testid={`promo-toggle-${c.code}`}>
                          {c.active ? <ToggleRight className="w-6 h-6" /> : <ToggleLeft className="w-6 h-6" />}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button onClick={() => openEdit(c)} className="text-[#FF1F87] hover:underline text-xs font-bold mr-3" data-testid={`promo-edit-${c.code}`}>Edit</button>
                        <button onClick={() => remove(c)} className="text-gray-400 hover:text-red-500 p-1 rounded-lg hover:bg-red-50" data-testid={`promo-delete-${c.code}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={closeForm}>
          <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white rounded-3xl p-6 w-full max-w-md space-y-4" data-testid="promo-form">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-extrabold text-2xl">{editingId ? "Edit Code" : "New Code"}</h2>
              <button type="button" onClick={closeForm} className="p-1 rounded-full hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>

            <label className="block">
              <span className="text-xs uppercase tracking-widest font-bold text-[#4B5563]">Code</span>
              <input
                type="text" required
                value={form.code}
                onChange={e => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s/g, "") })}
                placeholder="e.g. SUMMER25"
                disabled={!!editingId}
                className="input-base mt-1 uppercase tracking-widest disabled:bg-gray-100"
                data-testid="promo-form-code"
              />
            </label>

            <label className="block">
              <span className="text-xs uppercase tracking-widest font-bold text-[#4B5563]">Description <span className="normal-case tracking-normal text-[#9CA3AF]">(private notes)</span></span>
              <input
                type="text"
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="e.g. Instagram giveaway Aug 15"
                maxLength={120}
                className="input-base mt-1"
                data-testid="promo-form-description"
              />
            </label>

            <div>
              <p className="text-xs uppercase tracking-widest font-bold text-[#4B5563] mb-1">Type</p>
              <div className="grid grid-cols-3 gap-2">
                {KINDS.map(k => {
                  const active = form.kind === k.value;
                  const Icon = k.icon;
                  return (
                    <button
                      key={k.value} type="button"
                      onClick={() => setForm({ ...form, kind: k.value })}
                      className={`p-3 rounded-2xl border-2 text-xs font-bold transition-colors ${active ? "border-[#FF1F87] bg-[#FFE4EE] text-[#FF1F87]" : "border-gray-200 text-gray-600 hover:border-[#FF5A7E]"}`}
                      data-testid={`promo-form-kind-${k.value}`}
                    >
                      <Icon className="w-4 h-4 mx-auto mb-1" />
                      {k.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-gray-400 mt-1">{KINDS.find(k => k.value === form.kind)?.help}</p>
            </div>

            <label className="block">
              <span className="text-xs uppercase tracking-widest font-bold text-[#4B5563]">Value {form.kind === "percent_off" ? "(0-100)" : form.kind === "flat_off" ? "(dollars)" : "(pts per cookie)"}</span>
              <input
                type="number" min="0" step={form.kind === "flat_off" ? "0.01" : "1"}
                value={form.value}
                onChange={e => setForm({ ...form, value: e.target.value })}
                required
                className="input-base mt-1"
                data-testid="promo-form-value"
              />
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs uppercase tracking-widest font-bold text-[#4B5563] flex items-center gap-1"><CalIcon className="w-3 h-3" /> Expires</span>
                <input
                  type="date"
                  value={form.expires_at}
                  onChange={e => setForm({ ...form, expires_at: e.target.value })}
                  className="input-base mt-1"
                  data-testid="promo-form-expires"
                />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest font-bold text-[#4B5563]">Usage Cap (total)</span>
                <input
                  type="number" min="0"
                  value={form.usage_cap}
                  onChange={e => setForm({ ...form, usage_cap: e.target.value })}
                  placeholder="∞"
                  className="input-base mt-1"
                  data-testid="promo-form-cap"
                />
              </label>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} data-testid="promo-form-active" />
              Active (customers can use it)
            </label>

            <button type="submit" disabled={submitting} className="btn-pink w-full disabled:opacity-50" data-testid="promo-form-submit">
              {submitting ? "Saving…" : editingId ? "SAVE CHANGES" : "CREATE CODE"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
